/**
 * Planning work units — entity-configured list (§1E / §4.7).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ExternalLink,
  Loader2,
  Pencil,
  Table2,
  Trash2,
  Check,
  X,
  Table,
} from 'lucide-react';
import type { FacetDefinition, SortOption, TableColumnWithRender } from '../../../components/collection/types';
import {
  createWorkUnit,
  deleteWorkUnit,
  engineInputKindLabel,
  getPriorities,
  getWorkUnits,
  syncPlanningFromLiveItems,
  updateWorkUnit,
} from '../../../lib/planningEngineService';
import { PLANNING_SYNC_WU_NOTE_PREFIX, markWorkUnitManualOverride } from '../../../lib/itemToPlanningAdapter';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { usePlanningEngine } from './PlanningEngineContext';
import { PlanningEntityListPage } from './PlanningEntityListPage';
import type { Priority, PlanRecord, WorkUnit, WorkUnitState } from '../../../lib/planningEngineModule.types';
import {
  exportCsv,
  planDecisionClass,
  WORK_UNIT_STATES,
} from './planningEngineViewShared';
import { useResourceTypesStore } from '../../../stores/prioritization/resourceTypesStore';
import { useResourceRosterEnabled } from '../../../hooks/useResourceRosterEnabled';
import RequirementsPanel from './RequirementsPanel';
import { useItemHref } from '@/lib/itemLinkScope';

function planDecisionBadge(decision: string) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${planDecisionClass(decision as PlanRecord['planDecision'])}`}>
      {decision}
    </span>
  );
}

function WorkUnitDetailPanel({
  workUnit,
  planRecord,
  priority,
  onUpdated,
  onDeleted,
}: {
  workUnit: WorkUnit;
  planRecord?: PlanRecord;
  priority?: Priority;
  onUpdated: () => void;
  onDeleted: () => void;
}) {
  const itemHref = useItemHref();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(workUnit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rosterEnabled = useResourceRosterEnabled();
  const isSynced = workUnit.notes?.startsWith(PLANNING_SYNC_WU_NOTE_PREFIX);
  const sourceItemHref =
    workUnit.sourceType === 'prioritization_item' && workUnit.sourceId
      ? itemHref(workUnit.sourceId)
      : null;
  // Read once — resource types don't change mid-session
  const workProfiles = useMemo(() => useResourceTypesStore.getState().getEnabledTypes(), []);

  useEffect(() => {
    setDraft(workUnit);
    setEditing(false);
  }, [workUnit]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateWorkUnit(workUnit.id, {
        ...draft,
        notes: markWorkUnitManualOverride(draft.notes),
      });
      setEditing(false);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm('Delete this work unit?')) return;
    setSaving(true);
    try {
      await deleteWorkUnit(workUnit.id);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-5 h-full overflow-auto">
      <div className="flex items-start justify-between gap-3">
        <div>
          {sourceItemHref ? (
            <Link
              to={sourceItemHref}
              className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] hover:text-primary-600 dark:hover:text-primary-400 hover:underline"
              title="Open this item in Prioritization"
            >
              {workUnit.title}
            </Link>
          ) : (
            <h3 className="text-lg font-semibold">{workUnit.title}</h3>
          )}
          {isSynced && (
            <p className="text-xs text-[var(--text-ter)] mt-1">Synced from Prioritization item</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!editing && (
            <button type="button" onClick={() => setEditing(true)} className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
              <Pencil className="h-4 w-4" />
            </button>
          )}
          <button type="button" onClick={() => void remove()} disabled={saving} className="p-2 text-[var(--text-ter)] hover:text-[var(--coral)]">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      {error && <div className="text-sm text-[var(--coral)]">{error}</div>}
      {planRecord && (
        <div className="flex flex-wrap items-center gap-2">
          {planDecisionBadge(planRecord.planDecision)}
          <span className="text-xs text-[var(--text-ter)]">
            Readiness: {planRecord.readinessStatus} · Deps: {planRecord.dependencyStatus}
          </span>
        </div>
      )}
      {workUnit.sourceType === 'prioritization_item' && workUnit.sourceId && (
        <Link to={itemHref(workUnit.sourceId)} className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline">
          <ExternalLink className="h-3.5 w-3.5" />
          Open item
        </Link>
      )}
      {editing ? (
        <div className="space-y-3">
          <input className="w-full text-sm border rounded px-2 py-1.5" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <select className="w-full text-sm border rounded px-2 py-1.5" value={draft.workProfile} onChange={(e) => setDraft({ ...draft, workProfile: e.target.value })}>
            {workProfiles.map((rt) => <option key={rt.key} value={rt.key}>{rt.label}</option>)}
          </select>
          <input type="number" min={0} step={0.5} className="w-full text-sm border rounded px-2 py-1.5" value={draft.effortEstimateHours} onChange={(e) => setDraft({ ...draft, effortEstimateHours: Number(e.target.value) })} />
          <select className="w-full text-sm border rounded px-2 py-1.5" value={draft.state} onChange={(e) => setDraft({ ...draft, state: e.target.value as WorkUnitState })}>
            {WORK_UNIT_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="flex gap-2">
            <button type="button" onClick={() => void save()} disabled={saving || !draft.title.trim()} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-[var(--slate)] text-white">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              Save
            </button>
            <button type="button" onClick={() => { setDraft(workUnit); setEditing(false); }} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border">
              <X className="h-3 w-3" />
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div><dt className="text-[var(--text-ter)]">Priority</dt><dd className="font-medium">{priority?.name ?? workUnit.priorityId ?? '—'}</dd></div>
          <div><dt className="text-[var(--text-ter)]">Profile</dt><dd>{workUnit.workProfile}</dd></div>
          <div><dt className="text-[var(--text-ter)]">Effort (hrs)</dt><dd className="tabular-nums">{workUnit.effortEstimateHours}</dd></div>
          <div><dt className="text-[var(--text-ter)]">State</dt><dd>{workUnit.state}</dd></div>
        </dl>
      )}

      {/* What this work needs from whoever does it. A priority-level
          requirement applies here too, and is edited on the priority. */}
      {rosterEnabled && workUnit.projectId && (
        <div className="border-t border-[var(--rule)] dark:border-[var(--rule)] pt-4">
          <RequirementsPanel workspaceId={workUnit.projectId} workUnitId={workUnit.id} />
        </div>
      )}
    </div>
  );
}

// ─── Create modal ────────────────────────────────────────────────────────────

function CreateWorkUnitModal({
  projectId,
  priorities,
  onClose,
  onCreated,
}: {
  projectId: string;
  priorities: Priority[];
  onClose: () => void;
  onCreated: (wu: WorkUnit) => void;
}) {
  const [title, setTitle] = useState('');
  const [workProfile, setWorkProfile] = useState('engineering');
  const [effortHours, setEffortHours] = useState(8);
  const [state, setState] = useState<WorkUnitState>('backlog');
  const [priorityId, setPriorityId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const workProfiles = useMemo(() => useResourceTypesStore.getState().getEnabledTypes(), []);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const created = await createWorkUnit(projectId, {
        title: title.trim(),
        workProfile,
        state,
        effortEstimateHours: effortHours,
        priorityId: priorityId || null,
      });
      onCreated(created);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)]/80 w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-2">
            <Table className="h-5 w-5 text-[var(--text-ter)]" />
            <h3 className="text-base font-semibold text-[var(--text-pri)]">New work unit</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-[var(--hover-tint)]">
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Title <span className="text-[var(--coral)]">*</span></label>
            <input
              autoFocus
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleSave(); }}
              placeholder="e.g. Implement auth middleware"
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Profile</label>
              <select value={workProfile} onChange={(e) => setWorkProfile(e.target.value)} className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                {workProfiles.map((rt) => <option key={rt.key} value={rt.key}>{rt.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">State</label>
              <select value={state} onChange={(e) => setState(e.target.value as WorkUnitState)} className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                {WORK_UNIT_STATES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Effort (hours)</label>
              <input type="number" min={0} step={0.5} value={effortHours} onChange={(e) => setEffortHours(Number(e.target.value))} className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Priority</label>
              <select value={priorityId} onChange={(e) => setPriorityId(e.target.value)} className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="">— none —</option>
                {priorities.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>
          {error && <p className="text-sm text-[var(--coral)]">{error}</p>}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--rule)]">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-[var(--rule)] text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]">Cancel</button>
          <button type="button" onClick={() => void handleSave()} disabled={saving || !title.trim()} className="px-4 py-2 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)] disabled:opacity-50 flex items-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Create work unit
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PlanningWorkUnitsView() {
  const { projectId, activeScenario, planOutput, engineInput, scopedItemIds } = usePlanningEngine();
  const [workUnits, setWorkUnits] = useState<WorkUnit[]>([]);
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [loading, setLoading] = useState(true);
  const [, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const enabledWorkProfiles = useMemo(() => useResourceTypesStore.getState().getEnabledTypes(), []);

  const WU_FACETS = useMemo<FacetDefinition<string>[]>(() => [
    {
      key: 'state',
      label: 'State',
      options: WORK_UNIT_STATES.map((s) => ({ value: s, label: s.replace(/_/g, ' ') })),
    },
    {
      key: 'workProfile',
      label: 'Profile',
      options: enabledWorkProfiles.map((rt) => ({ value: rt.key, label: rt.label })),
    },
  ], [enabledWorkProfiles]);

  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const fetchRelationships = useItemRelationshipsStore((s) => s.fetchFromSupabase);

  const planRecordByWuId = useMemo(
    () => new Map((planOutput?.planRecords ?? []).map((r) => [r.workUnit.id, r])),
    [planOutput],
  );
  const priorityById = useMemo(() => new Map(priorities.map((p) => [p.id, p])), [priorities]);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [wus, prs] = await Promise.all([getWorkUnits(projectId), getPriorities(projectId)]);
      setWorkUnits(wus);
      setPriorities(prs);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const handleSync = useCallback(async () => {
    if (!projectId) return;
    setSyncing(true);
    setError(null);
    try {
      await Promise.all([fetchItems(), fetchRelationships()]);
      const liveItems = useItemsStore.getState().items;
      const liveEdges = useItemRelationshipsStore.getState().edges;
      await syncPlanningFromLiveItems(projectId, activeScenario?.id ?? null, liveItems, liveEdges);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSyncing(false);
    }
  }, [projectId, activeScenario?.id, fetchItems, fetchRelationships, load]);

  // Auto-sync from live items on mount so work units appear without a manual step.
  const didAutoSync = useRef(false);
  useEffect(() => {
    if (didAutoSync.current) return;
    didAutoSync.current = true;
    void handleSync();
  }, [handleSync]);


  // Filter to the active input scope.
  // Work units synced from items have sourceId === item.id (set by itemToWorkUnit).
  // Manually created work units (sourceId = null) inherit scope from their parent priority.
  const visibleWorkUnits = useMemo(
    () =>
      scopedItemIds
        ? workUnits.filter(
            (w) =>
              (w.sourceId != null && scopedItemIds.has(w.sourceId)) ||
              (w.sourceId == null && w.priorityId != null && scopedItemIds.has(w.priorityId)),
          )
        : workUnits,
    [workUnits, scopedItemIds],
  );

  const scopeBanner = scopedItemIds
    ? `Scoped to ${engineInputKindLabel(engineInput.type)} — showing ${visibleWorkUnits.length} of ${workUnits.length} work units`
    : null;

  const sortOptions: SortOption<'title' | 'effortEstimateHours'>[] = [
    { field: 'title', label: 'Title', defaultDirection: 'asc' },
    { field: 'effortEstimateHours', label: 'Effort', defaultDirection: 'desc' },
  ];

  const tableColumns: TableColumnWithRender<WorkUnit>[] = useMemo(
    () => [
      { key: 'title', label: 'Title', sortable: true, minWidth: 220, flex: true, render: (w) => <span className="font-medium truncate block">{w.title}</span> },
      { key: 'priority', label: 'Priority', minWidth: 120, render: (w) => {
        if (!w.priorityId) return <span className="text-[var(--text-ter)]">—</span>;
        const p = priorityById.get(w.priorityId);
        if (!p) return <span className="text-[var(--text-ter)] text-xs font-mono">{w.priorityId.slice(0, 8)}</span>;
        const rank = p.forceRank != null ? `#${p.forceRank}` : null;
        return (
          <span className="flex items-center gap-1.5 min-w-0">
            {rank && (
              <span className="shrink-0 text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] tabular-nums">
                {rank}
              </span>
            )}
            <span className="truncate text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">{p.name}</span>
          </span>
        );
      } },
      { key: 'profile', label: 'Profile', minWidth: 96, render: (w) => w.workProfile },
      { key: 'effort', label: 'Hrs', align: 'right', minWidth: 64, render: (w) => <span className="tabular-nums">{w.effortEstimateHours}</span> },
      { key: 'decision', label: 'Decision', minWidth: 120, render: (w) => { const rec = planRecordByWuId.get(w.id); return rec ? planDecisionBadge(rec.planDecision) : '—'; } },
    ],
    [planRecordByWuId, priorityById],
  );

  return (
    <>
    <PlanningEntityListPage
      items={visibleWorkUnits}
      loading={loading}
      getId={(w) => w.id}
      title="Work Units"
      subtitle={scopeBanner ?? 'Atomic units for capacity planning — one placeholder per active item on sync'}
      icon={<Table2 className="h-4 w-4 text-[var(--text-ter)]" />}
      searchFields={['title', 'workProfile']}
      sortOptions={sortOptions}
      defaultSort={{ field: 'title', direction: 'asc' }}
      groupOptions={[
        {
          field: 'priorityId',
          label: 'Priority',
          getGroupLabel: (key) => {
            const p = priorityById.get(key);
            if (!p) return key;
            return p.forceRank != null ? `#${p.forceRank} — ${p.name}` : p.name;
          },
        },
        { field: 'workProfile', label: 'Profile' },
        { field: 'state', label: 'State' },
      ]}
      facets={WU_FACETS}
      tableColumns={tableColumns}
      renderCard={(w, { selected }) => (
        <div className="p-4">
          <p className="font-medium truncate">{w.title}</p>
          <p className="text-xs text-[var(--text-ter)] mt-1">{w.workProfile} · {w.effortEstimateHours}h · {w.state}</p>
          {selected && <p className="text-xs text-[var(--text-ter)] mt-2">Selected</p>}
        </div>
      )}
      renderDetail={(w) => (
        <WorkUnitDetailPanel
          workUnit={w}
          planRecord={planRecordByWuId.get(w.id)}
          priority={w.priorityId ? priorityById.get(w.priorityId) : undefined}
          onUpdated={() => void load()}
          onDeleted={() => { setSelectedId(null); void load(); }}
        />
      )}
      selectedId={selectedId}
      onSelectionChange={setSelectedId}
      urlParamName="workUnit"
      onRefresh={() => void handleSync()}
      onExport={() => exportCsv(`planning-work-units-${new Date().toISOString().slice(0, 10)}.csv`, ['id', 'title', 'priority_id', 'work_profile', 'effort_hours', 'state'], workUnits.map((w) => [w.id, w.title, w.priorityId ?? '', w.workProfile, String(w.effortEstimateHours), w.state]))}
      onCreate={() => setCreating(true)}
      createLabel="New work unit"
      emptyTitle={
        workUnits.length > 0 && scopedItemIds
          ? `Scope shows 0 of ${workUnits.length} work units`
          : activeScenario?.planningMode === 'priority_estimate'
            ? 'No work units in this scenario'
            : 'No work units yet'
      }
      emptyDescription={
        workUnits.length > 0 && scopedItemIds
          ? `This scenario’s Input Scope (${engineInputKindLabel(engineInput.type)}) has no matching work units. All Live Items is locked while the scenario is hard-scoped.`
          : activeScenario?.planningMode === 'priority_estimate'
            ? 'This scenario is priority-estimate — Launch Calendar places priorities directly. Work units appear when the scenario uses work-unit mode or you sync WUs from items.'
            : 'Work units sync automatically from your items in Prioritization. Add items there and they will appear here.'
      }
      emptyAction={undefined}
      error={error}
      activeScenario={activeScenario}
      itemLabel="work unit"
    />
    {creating && projectId && (
      <CreateWorkUnitModal
        projectId={projectId}
        priorities={priorities}
        onClose={() => setCreating(false)}
        onCreated={(wu) => {
          setWorkUnits((prev) => [...prev, wu]);
          setSelectedId(wu.id);
          setCreating(false);
          void load();
        }}
      />
    )}
    </>
  );
}
